import type { NonogramCell } from './types';

export type LinePattern = {
  cells: boolean[];
  runs: Array<{ start: number; end: number }>;
};

export function deriveLineClues(line: readonly boolean[]): number[] {
  const clues: number[] = [];
  let run = 0;
  for (const filled of [...line, false]) {
    if (filled) run += 1;
    else if (run > 0) {
      clues.push(run);
      run = 0;
    }
  }
  return clues.length > 0 ? clues : [0];
}

export function deriveNonogramClues(
  solution: readonly boolean[],
  width: number,
  height: number,
): { rowClues: number[][]; columnClues: number[][] } {
  const rowClues = Array.from({ length: height }, (_, row) =>
    deriveLineClues(solution.slice(row * width, (row + 1) * width)),
  );
  const columnClues = Array.from({ length: width }, (_, column) =>
    deriveLineClues(Array.from({ length: height }, (_, row) => solution[row * width + column])),
  );
  return { rowClues, columnClues };
}

export function generateLinePatterns(length: number, clues: readonly number[]): LinePattern[] {
  const normalized = clues.length === 1 && clues[0] === 0 ? [] : [...clues];
  if (normalized.some((clue) => clue <= 0)) return [];
  const patterns: LinePattern[] = [];

  function place(clueIndex: number, cursor: number, cells: boolean[], runs: LinePattern['runs']) {
    if (clueIndex === normalized.length) {
      patterns.push({ cells, runs });
      return;
    }
    const remaining = normalized.slice(clueIndex + 1).reduce((total, clue) => total + clue + 1, 0);
    const size = normalized[clueIndex];
    for (let start = cursor; start + size + remaining <= length; start += 1) {
      const nextCells = [...cells];
      for (let offset = 0; offset < size; offset += 1) nextCells[start + offset] = true;
      place(clueIndex + 1, start + size + 1, nextCells, [
        ...runs,
        { start, end: start + size - 1 },
      ]);
    }
  }

  place(
    0,
    0,
    Array.from({ length }, () => false),
    [],
  );
  return patterns;
}

export function lineMatchesMarks(
  pattern: readonly boolean[],
  marks: readonly NonogramCell[],
): boolean {
  return pattern.every((filled, index) => marks[index] === 0 || (marks[index] === 1) === filled);
}

export function completedLineClues(
  marks: readonly NonogramCell[],
  clues: readonly number[],
): boolean[] {
  if (clues.length === 1 && clues[0] === 0) {
    return [marks.every((mark) => mark !== 1)];
  }
  const candidates = generateLinePatterns(marks.length, clues).filter((pattern) =>
    lineMatchesMarks(pattern.cells, marks),
  );
  if (candidates.length === 0) return clues.map(() => false);
  return clues.map((_, clueIndex) => {
    const first = candidates[0].runs[clueIndex];
    return (
      candidates.every(
        (candidate) =>
          candidate.runs[clueIndex].start === first.start &&
          candidate.runs[clueIndex].end === first.end,
      ) && marks.slice(first.start, first.end + 1).every((mark) => mark === 1)
    );
  });
}

export function isLineOverfilled(
  marks: readonly NonogramCell[],
  clues: readonly number[],
): boolean {
  const allowance = clues.length === 1 && clues[0] === 0 ? 0 : clues.reduce((a, b) => a + b, 0);
  if (marks.filter((mark) => mark === 1).length > allowance) return true;
  let run = 0;
  const longestAllowed = Math.max(...clues);
  for (const mark of [...marks, 0] as NonogramCell[]) {
    if (mark === 1) run += 1;
    else {
      if (run > longestAllowed) return true;
      run = 0;
    }
  }
  return false;
}

type Domains = { rows: LinePattern[][]; columns: LinePattern[][] };

function propagate(domains: Domains, width: number, height: number): boolean {
  let changed = true;
  while (changed) {
    changed = false;
    for (let row = 0; row < height; row += 1) {
      const filtered = domains.rows[row].filter((pattern) =>
        pattern.cells.every((filled, column) =>
          domains.columns[column].some((candidate) => candidate.cells[row] === filled),
        ),
      );
      if (filtered.length === 0) return false;
      if (filtered.length !== domains.rows[row].length) {
        domains.rows[row] = filtered;
        changed = true;
      }
    }
    for (let column = 0; column < width; column += 1) {
      const filtered = domains.columns[column].filter((pattern) =>
        pattern.cells.every((filled, row) =>
          domains.rows[row].some((candidate) => candidate.cells[column] === filled),
        ),
      );
      if (filtered.length === 0) return false;
      if (filtered.length !== domains.columns[column].length) {
        domains.columns[column] = filtered;
        changed = true;
      }
    }
  }
  return true;
}

function cloneDomains(domains: Domains): Domains {
  return {
    rows: domains.rows.map((domain) => [...domain]),
    columns: domains.columns.map((d) => [...d]),
  };
}

export type NonogramSolutionAnalysis = {
  count: number;
  solution: boolean[] | null;
  solvedByPropagation: boolean;
  searchNodes: number;
};

export type NonogramDeductionRound = {
  forcedCells: number;
  rowForcedCells: number;
  columnForcedCells: number;
  remainingUnknown: number;
};

export type NonogramLogicTrace = {
  solved: boolean;
  valid: boolean;
  propagationRounds: number;
  openingDeductions: number;
  narrowDeductionSteps: number;
  maximumStallSize: number;
  rowDeductionCount: number;
  columnDeductionCount: number;
  guesses: number;
  difficulty: number;
  rounds: NonogramDeductionRound[];
};

function consensus(domain: readonly LinePattern[], index: number): boolean | null {
  if (domain.length === 0) return null;
  const value = domain[0].cells[index];
  return domain.every((pattern) => pattern.cells[index] === value) ? value : null;
}

export function traceNonogramLogic(
  rowClues: readonly number[][],
  columnClues: readonly number[][],
): NonogramLogicTrace {
  const height = rowClues.length;
  const width = columnClues.length;
  const domains: Domains = {
    rows: rowClues.map((clues) => generateLinePatterns(width, clues)),
    columns: columnClues.map((clues) => generateLinePatterns(height, clues)),
  };
  const marks = Array.from({ length: width * height }, () => null as boolean | null);
  const rounds: NonogramDeductionRound[] = [];
  let valid = true;
  let maximumStallSize = 0;
  let rowDeductionCount = 0;
  let columnDeductionCount = 0;

  for (let roundIndex = 0; roundIndex < width * height; roundIndex += 1) {
    for (let row = 0; row < height; row += 1) {
      domains.rows[row] = domains.rows[row].filter((pattern) =>
        pattern.cells.every((filled, column) => {
          const mark = marks[row * width + column];
          return mark === null || mark === filled;
        }),
      );
    }
    for (let column = 0; column < width; column += 1) {
      domains.columns[column] = domains.columns[column].filter((pattern) =>
        pattern.cells.every((filled, row) => {
          const mark = marks[row * width + column];
          return mark === null || mark === filled;
        }),
      );
    }
    if (!propagate(domains, width, height)) {
      valid = false;
      break;
    }

    let rowForcedCells = 0;
    let columnForcedCells = 0;
    const pending = new Map<number, boolean>();
    const rowPending = new Set<number>();
    const columnPending = new Set<number>();
    for (let row = 0; row < height; row += 1) {
      for (let column = 0; column < width; column += 1) {
        const index = row * width + column;
        if (marks[index] !== null) continue;
        const value = consensus(domains.rows[row], column);
        if (value !== null) {
          pending.set(index, value);
          rowPending.add(index);
        }
      }
    }
    for (let column = 0; column < width; column += 1) {
      for (let row = 0; row < height; row += 1) {
        const index = row * width + column;
        if (marks[index] !== null) continue;
        const value = consensus(domains.columns[column], row);
        if (value === null) continue;
        const existing = pending.get(index);
        if (existing !== undefined && existing !== value) {
          valid = false;
          break;
        }
        pending.set(index, value);
        columnPending.add(index);
      }
      if (!valid) break;
    }
    if (!valid) break;
    for (const [index, value] of pending) {
      marks[index] = value;
    }
    rowForcedCells = rowPending.size;
    columnForcedCells = columnPending.size;
    rowDeductionCount += rowForcedCells;
    columnDeductionCount += columnForcedCells;
    const remainingUnknown = marks.filter((mark) => mark === null).length;
    rounds.push({
      forcedCells: pending.size,
      rowForcedCells,
      columnForcedCells,
      remainingUnknown,
    });
    if (remainingUnknown === 0 || pending.size === 0) {
      if (pending.size === 0) maximumStallSize = Math.max(maximumStallSize, remainingUnknown);
      break;
    }
    if (pending.size <= 1) maximumStallSize = Math.max(maximumStallSize, remainingUnknown);
  }
  const solved = valid && marks.every((mark) => mark !== null);
  const narrowDeductionSteps = rounds.filter((round) => round.forcedCells <= 1).length;
  const guesses = solved ? 0 : 1;
  const propagationRounds = rounds.filter((round) => round.forcedCells > 0).length;
  const difficulty =
    propagationRounds + narrowDeductionSteps * 2 + maximumStallSize * 3 + guesses * 100;
  return {
    solved,
    valid,
    propagationRounds,
    openingDeductions: rounds[0]?.forcedCells ?? 0,
    narrowDeductionSteps,
    maximumStallSize,
    rowDeductionCount,
    columnDeductionCount,
    guesses,
    difficulty,
    rounds,
  };
}

export function solveNonogram(
  rowClues: readonly number[][],
  columnClues: readonly number[][],
  limit = 2,
): NonogramSolutionAnalysis {
  const height = rowClues.length;
  const width = columnClues.length;
  const initial: Domains = {
    rows: rowClues.map((clues) => generateLinePatterns(width, clues)),
    columns: columnClues.map((clues) => generateLinePatterns(height, clues)),
  };
  const initialValid = propagate(initial, width, height);
  const solvedByPropagation = initialValid && initial.rows.every((domain) => domain.length === 1);
  let count = 0;
  let solution: boolean[] | null = null;
  let searchNodes = 0;

  function search(domains: Domains) {
    searchNodes += 1;
    if (count >= limit || !propagate(domains, width, height)) return;
    let axis: 'rows' | 'columns' | null = null;
    let target = -1;
    let smallest = Number.POSITIVE_INFINITY;
    for (const candidateAxis of ['rows', 'columns'] as const) {
      for (let index = 0; index < domains[candidateAxis].length; index += 1) {
        const domain = domains[candidateAxis][index];
        if (domain.length > 1 && domain.length < smallest) {
          axis = candidateAxis;
          target = index;
          smallest = domain.length;
        }
      }
    }
    if (axis === null) {
      count += 1;
      solution ??= domains.rows.flatMap((domain) => domain[0].cells);
      return;
    }
    const branchAxis = axis;
    for (const pattern of domains[branchAxis][target]) {
      const next = cloneDomains(domains);
      next[branchAxis][target] = [pattern];
      search(next);
      if (count >= limit) return;
    }
  }

  if (initialValid) search(cloneDomains(initial));
  return { count, solution, solvedByPropagation, searchNodes };
}
